import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHero, useQuery, SortBar, Empty, Rating, FilterGroup, Check, toggleIn } from '../../shared/components/Common';
import { SmartImg } from '../../shared/components/Layout';
import Icon from '../../shared/components/Icons';
import { formatPrice } from '../../shared/data/common';
import { usePackages } from './PackagesContext';

const durations = [['short', 'Up to 4 nights', 0, 4], ['mid', '5 – 6 nights', 5, 6], ['long', '7+ nights', 7, 99]];

const regionTitles = { india: 'India holiday packages', international: 'International holiday packages' };
const themeTitles = { 'Group Tour': 'Group tours', Pilgrimage: 'Religious tours' };

export default function PackageList() {
  const q = useQuery();
  const { packages, themes } = usePackages();
  const { search } = useLocation();
  const [theme, setTheme] = useState(q.theme || 'All');
  const [sort, setSort] = useState('popular');
  const [dur, setDur] = useState([]);
  const [withFlights, setWithFlights] = useState(false);

  const list = useMemo(() => {
    let r = packages.filter((p) =>
      (!q.destination || p.destination === q.destination) &&
      (!q.region || p.international === (q.region === 'international')) &&
      (theme === 'All' || p.theme === theme) &&
      (!withFlights || p.includes.includes('Flights')) &&
      (!dur.length || dur.some((k) => { const [, , a, b] = durations.find((d) => d[0] === k); return p.nights >= a && p.nights <= b; })));
    if (sort === 'popular') r = [...r].sort((a, b) => b.rating - a.rating);
    if (sort === 'low') r = [...r].sort((a, b) => a.price - b.price);
    if (sort === 'short') r = [...r].sort((a, b) => a.nights - b.nights);
    return r;
  }, [packages, q.destination, q.region, theme, sort, dur, withFlights]);

  return (
    <>
      <PageHero tab="packages" title={q.destination ? `${q.destination} holiday packages` : regionTitles[q.region] || themeTitles[q.theme] || 'Holiday packages'}
        subtitle="Flights, stays, transfers & tours bundled — save up to 25%" query={q} />
      <div className="container listing">
        <aside className="filters">
          <div className="filters-head"><b>Filters</b><button className="link small" onClick={() => { setTheme('All'); setDur([]); setWithFlights(false); }}>Clear all</button></div>
          <FilterGroup title="Theme">
            {themes.map((t) => (
              <label key={t} className="check"><input type="radio" name="theme" checked={theme === t} onChange={() => setTheme(t)} /><span>{t}</span></label>
            ))}
          </FilterGroup>
          <FilterGroup title="Duration">
            {durations.map(([k, l]) => <Check key={k} label={l} checked={dur.includes(k)} onChange={(on) => setDur(toggleIn(dur, k, on))} />)}
          </FilterGroup>
          <FilterGroup title="Inclusions">
            <Check label="Flights included" checked={withFlights} onChange={setWithFlights} />
          </FilterGroup>
        </aside>
        <div className="results">
          <SortBar count={list.length} label="packages" sort={sort} setSort={setSort}
            options={[['popular', 'Popular'], ['low', 'Price ↑'], ['short', 'Duration']]} />
          {!list.length && <Empty text="No packages here yet — try another destination or theme." />}
          <div className="card-grid two">
            {list.map((p) => {
              const off = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
              return (
                <Link key={p.id} to={`/packages/${p.id}${search}`} className="card hover">
                  <div className="card-img tall">
                    <SmartImg src={p.image} alt={p.title} />
                    <span className="badge badge-dark">{p.nights}N / {p.nights + 1}D</span>
                    {off > 0 && <span className="badge badge-coral right">{off}% OFF</span>}
                  </div>
                  <div className="card-body">
                    <div className="row-top"><span className="tag">{p.theme}</span><Rating value={p.rating} reviews={p.reviews} /></div>
                    <h3>{p.title}</h3>
                    <span className="muted small"><Icon name="pin" size={12} /> {p.cities.join(' → ')}</span>
                    <div className="includes">
                      {p.includes.map((i) => <span key={i}><Icon name="check" size={12} strokeWidth={2.5} /> {i}</span>)}
                    </div>
                    <div className="price-row">
                      <div>
                        {p.oldPrice && <s className="muted small">{formatPrice(p.oldPrice)}</s>}
                        <div>{p.enquiryOnly && <span className="muted small">From </span>}<b className="price big">{formatPrice(p.price)}</b><span className="muted small"> /person</span></div>
                      </div>
                      <span className="btn btn-primary">View Details</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
